import React from "react";
import { FiCheckCircle, FiClock, FiFileText, FiFlag, FiLayout, FiLayers, FiRepeat, FiSliders, FiTarget, FiZap } from "react-icons/fi";
import {
  LuSettings,
  LuUsers,
  LuSmartphone,
  LuShieldCheck,
  LuPercent,
  LuShield,
  LuBuilding2,
  LuShoppingBag,
  LuClock,
  LuWallet,
  LuCpu,
  LuClipboardList,
  LuPalette,
  LuCode,
  LuBug,
  LuRocket,
} from "react-icons/lu";

// 1. Hero Block Configuration
export const wireframingHeroData = {
  backgroundImage: "/images/serviceBanners/Wireframing&Prototyping.webp",
  whiteImage: "/images/serviceBanners/WireframingPrototypingW.webp",
  subTitle: "Creating Better User Flows ",
  title: (
    <>
      Modern <br />
      <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Wireframing & Prototyping</span> Solutions
    </>
  ),
  description:
    "We design interactive wireframes and prototypes that refine user flows, improve usability, and validate product experiences early, ensuring better design decisions and smoother development before final product execution.",
  primaryButton: {
    title: "Book Prototype Call",
    onClick: () => console.log("Prototype Call clicked"),
  },
  secondaryButton: {
    title: "View Prototype Demos",
    onClick: () => console.log("View Demos clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "User Flow Mapping",
      desc: "Structured wireframes built to improve navigation and usability.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Interactive Prototypes",
      desc: "Clickable prototypes designed to validate ideas before development.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Experience Planning",
      desc: "Clear layouts focused on better user journeys and functionality.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Design Validation",
      desc: "Test concepts early to reduce revisions and improve usability.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/reactjs.svg", // Re-using existing SVG asset
      top: "16%",
      right: "8%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(168,85,247,0.35)",
      iconGlow: "rgba(168,85,247,0.9)",
    },
  ],
};

// 2. Stats Block
export const wireframingStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block (WhyChooseUs Data)
export const wireframingSolutionsAndProcessData = {
  buildSubtitle: "OUR CAPABILITIES",
  buildTitle: "Custom Wireframing & Prototyping Solutions for Better User Experiences",
  buildCards: [
    {
      title: "Low-Fidelity Wireframes",
      desc: "Create structured layouts to plan user flows and screen hierarchy.",
      icon: FiLayout,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Interactive Prototypes",
      desc: "Build clickable prototypes to test ideas before development.",
      icon: FiSliders,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "E-Commerce Prototyping",
      desc: "Design seamless shopping experiences through user-focused wireframes.",
      icon: FiLayers,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "On-Demand Experience Flows",
      desc: "Prototype booking and service journeys for smoother interactions.",
      icon: FiFlag,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "FinTech Experience Design",
      desc: "Plan secure and intuitive financial user experiences.",
      icon: FiLayers,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "Custom Wireframing Solutions",
      desc: "Tailored prototypes designed around business and user needs.",
      icon: FiLayout,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "OUR METHODOLOGY",
  processTitle: "Our Wireframing & Prototyping Process",
  processSteps: [
    {
      step: "01",
      title: "Discovery & Research",
      description: "Understand user goals, business needs, and project requirements.",
      icon: LuUsers,
    },
    {
      step: "02",
      title: "Structure Planning",
      description: "Plan layouts, screen hierarchy, and user flow direction.",
      icon: LuPalette,
    },
    {
      step: "03",
      title: "Wireframe Creation",
      description: "Build structured wireframes to map core functionality.",
      icon: LuCode,
    },
    {
      step: "04",
      title: "Interactive Prototyping",
      description: "Create clickable prototypes to visualize user interactions.",
      icon: LuRocket,
    },
    {
      step: "05",
      title: "Usability Testing",
      description: "Validate flows and identify improvements before development.",
      icon: LuPalette,
    },
    {
      step: "06",
      title: "Refinement & Handoff",
      description: "Optimize prototypes and prepare clear design direction for development.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE OUR WIREFRAMING & PROTOTYPING SERVICES?",
  benefitsTitle: "Why Businesses Choose Wireframing & Prototyping Solutions",
  benefits: [
    "Clear user flow planning for better experiences",
    "Faster design validation before development starts",
    "Interactive prototypes for smarter decision-making",
    "Improved usability through early testing",
    "Efficient design workflows with fewer revisions",
    "Scalable wireframes for complex digital products",
    "Cost-effective planning that reduces development errors",
  ],
  logoImage: "/images/serviceBanners/Wireframing&Prototyping.webp",
  whiteLogoImage: "/images/serviceBanners/WireframingPrototypingW.webp",
};

// 4. Portfolio Data
export const wireframingPortfolioData = {
  subtitle: "OUR PROTOTYPES",
  title: "Experiences Designed for Validation",
  buttonText: "View Interactive Demos",
  buttonLink: "#",
  apps: [
    {
      title: "SaaS Workspace",
      badge: "Hi-Fi Prototype",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "Full interaction model for a multi-tenant project management tool.",
      downloads: "Figma Link",
      rating: "5.0",
      image: "/images/uiux/wireframing/High-Fidelity-Prototypes.png",
    },
  ],
};

// 5. Technologies Data
export const wireframingTechnologiesData = {
  techTitle: "OUR DESIGN STACK",
  technologies: [
    { name: "Figma", icon: "Palette" },
    { name: "FigJam", icon: "Users" },
    { name: "Principle", icon: "Zap" },
    { name: "Lottie", icon: "Layout" },
    { name: "Storybook", icon: "Code" },
  ],
  trustTitle: "TRUSTED BY DESIGN TEAMS",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

export const wireframingAboutData = [
  {
    title: "Wireframing and Prototyping Services for Smarter Product Planning",
    content: (
      <>
        Strong digital products begin with better planning before development starts. Effective{" "}
        <b className="text-text-primary font-semibold">wireframing and prototyping services</b> help businesses visualize user journeys, identify usability gaps, and reduce costly
        revisions during development. Clear product structure improves communication between teams and creates better alignment around user expectations. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, planning frameworks are created to help businesses transform ideas into practical digital experiences
        while minimizing confusion during execution.
      </>
    ),
  },
  {
    title: "Interactive Prototype Design for Better User Validation",
    content: (
      <>
        Building products without testing user interactions often leads to poor adoption and usability challenges. Reliable{" "}
        <b className="text-text-primary font-semibold">interactive prototype design</b> allows businesses to visualize workflows, gather feedback, and improve decision-making
        before development investments increase. Companies investing in <b className="text-text-primary font-semibold">mobile app wireframe design</b> often gain stronger clarity
        around navigation patterns, functionality, and customer expectations. Tycho Technologies helps businesses validate digital ideas through structured planning that improves
        usability and supports better long-term product performance.
      </>
    ),
  },
  {
    title: "UI UX Prototyping Company Focused on User Experience",
    content: (
      <>
        Digital experiences perform better when user journeys are tested early instead of fixed later. A trusted{" "}
        <b className="text-text-primary font-semibold">ui ux prototyping company</b> helps businesses reduce friction, improve customer understanding, and build stronger product
        confidence before launch. Organizations often improve planning outcomes by connecting product structure with{" "}
        <a href="https://tychotechnologies.com/ui-design-systems-india" className="text-cyan-400 underline hover:text-cyan-300">
          design system services
        </a>{" "}
        to create more consistent and scalable digital experiences across platforms.
      </>
    ),
  },
  {
    title: "Why Businesses Choose Wireframing Services India",
    content: (
      <>
        The demand for reliable <b className="text-text-primary font-semibold">wireframing services india</b> continues to grow as businesses prioritize user-focused product
        development. Companies looking for a trusted <b className="text-text-primary font-semibold">ui ux prototyping company in india</b> often seek better planning, faster
        validation, and reduced development uncertainty. Businesses partnering with Tycho Technologies benefit from tailored prototyping strategies designed around business
        objectives and user expectations. Many organizations also strengthen digital planning with{" "}
        <a href="https://tychotechnologies.com/ux-research-strategy-india" className="text-cyan-400 underline hover:text-cyan-300">
          ux strategy services
        </a>{" "}
        to improve product clarity and long-term usability outcomes.
      </>
    ),
  },
];

// 7. FAQs (Preserved)
export const wireframingFaqs = [
  {
    q: "What is the difference between wireframing and prototyping?",
    a: "Wireframes define structure and layout, while prototypes simulate interactions and user journeys. Together they help validate UX decisions before development starts.",
  },
  {
    q: "Why is design prototyping important for my app or website?",
    a: "Design prototyping helps visualize your app or website before development. It allows you to test functionality, user flow, and interface design, ensuring a better final product and saving time and development costs.",
  },
  {
    q: "What do design prototyping services in India include?",
    a: "Design prototyping services in India include creating wireframes, interactive mockups, user flow diagrams, and clickable prototypes. These services help businesses refine their app or website design and provide developers with a clear blueprint for development.",
  },
];
